/**
 * Inbox column role filter — the ONE place the shared pipeline board
 * narrows what a role sees, and it applies to the Inbox column only.
 *
 * Rule (Adviser decision, 22 September 2026):
 *   The Inbox column (stage key 'submitted' — displayed as "Inbox",
 *   CLAUDE.md §7.1) shows only the card types the ACTIVE role acts on:
 *
 *     adviser                 → all four types: file folders, preparation
 *                               requests, review calendar cards, referrals
 *     assistant               → file folders only
 *     new_business_assistant  → preparation requests only
 *
 *   Every other column (In Progress, Paused, Awaiting Signature, Signed &
 *   Sent to New Business, File Completed) still shows every card type to
 *   every role, exactly as before (§30). Work that has already started is
 *   the team's shared picture; the Inbox is each role's own queue.
 *
 * This is VISIBILITY only. Action rights (canOpenFiles, canOpenPrep,
 * canOpenReviews, canOpenReferrals in SharedPipeline.jsx) are unchanged
 * everywhere — a card this filter leaves in place is still openable or
 * inert exactly as it was.
 *
 * Pure and side-effect free: takes the four column arrays the board has
 * already assembled for the submitted column and returns the same four
 * keys with the non-applicable ones emptied. It never fetches, never
 * counts toward any statistic (§21.1), and never touches the arrays it is
 * given. SharedPipeline applies it BEFORE the header count badge and the
 * empty-state check so the count always matches what is rendered.
 *
 * Any role not listed (including an unresolved / null role) falls back to
 * an empty Inbox rather than the full one — the safe default for a queue.
 */

/** The four card-type keys the board assembles per column. */
const CARD_TYPES = ['files', 'preps', 'reviews', 'referrals'];

/**
 * Card types each role acts on from the Inbox column. The Adviser sees
 * everything; each assistant sees only the queue that is theirs to draw.
 */
export const INBOX_VISIBLE_TYPES_BY_ROLE = {
  adviser: ['files', 'preps', 'reviews', 'referrals'],
  assistant: ['files'],
  new_business_assistant: ['preps'],
};

/**
 * @param {string|null} role — the ACTIVE role from useAuth()
 *   ('adviser' | 'assistant' | 'new_business_assistant')
 * @param {{ files?: Array, preps?: Array, reviews?: Array, referrals?: Array }} columns
 *   — the Inbox column's four card arrays as already assembled by the board
 * @returns {{ files: Array, preps: Array, reviews: Array, referrals: Array }}
 *   — the same four keys; types the role does not act on are `[]`
 */
export function filterInboxByRole(role, columns = {}) {
  const visible = INBOX_VISIBLE_TYPES_BY_ROLE[role] || [];
  const out = {};
  for (const type of CARD_TYPES) {
    const list = Array.isArray(columns[type]) ? columns[type] : [];
    out[type] = visible.includes(type) ? list : [];
  }
  return out;
}
